import moment from "moment";
import {
  dateTimeFormatted,
  formatAppointmentDateTime,
  formattedDate,
} from "../utils/dateUtils";
import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { preventDuplicateRequest } from "../utils/requestManager";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

export interface AssignedUser {
  userId: number;
  userName: string;
  fullName: string;
  emailId: string;
  departmentName: string | null;
  genderName: string | null;
  phoneNumber: string | null;
  userTypeName: string;
  userRoleName: string;
  teamName: string | null;
}

export interface UpdateAssignedToRequest {
  subjectQmeScheduleIDs: string;
  assignTo: number;
}

const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-US");
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};

const transcriber = process.env.REACT_APP_CASE_URL || "";
if (!transcriber) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const TranscriberAPI = api(transcriber);

const getTranscriberSummary = async (transcriberData: any): Promise<any> => {
  const key = `getTranscriberSummary-${JSON.stringify(transcriberData)}`; 

  return preventDuplicateRequest(key, async () => { 
    try {
      const response = await TranscriberAPI.post<ApiResponse<any>>(
        "/api/transcriber/summary",
        transcriberData,
      );

      return response.data?.result;
    } catch (error) {
      console.error("Error getting Data:", error);
      throw new Error(
        "Failed to get the data. Please check the data and try again.",
      );
    }
  });
};

const getTranscriberSimpleSearch = async (
  transcriberData: any,
): Promise<any> => {
  const key = `getTranscriberSimpleSearch-${JSON.stringify(transcriberData)}`; 

  return preventDuplicateRequest(key, async () => { 
    try {
      const response = await TranscriberAPI.post<ApiResponse<any>>(
        "/api/transcriber/schedule",
        transcriberData,
      );

      const updatedResult = response.data?.result.map((data: any) => ({
        ...data,
        subjectqmescheduleid: data.subjectQmeScheduleId,
        subjectQmeScheduleID: data.subjectQmeScheduleId,
        appointmentDate: formatAppointmentDateTime(data.appointmentDate),
        dueDate: formattedDate(data.dueDate),
        format_dueDate: formatAppointmentDateTime(data.dueDate),
      }));

      return updatedResult;
    } catch (error) {
      console.error("Error getting Data:", error);
      throw new Error(
        "Failed to get the data. Please check the data and try again.",
      );
    }
  });
};

const GetAssignedToList = async (): Promise<
  ApiResponse<AssignedUser[]> | undefined
> => {
  try {
    const response = await TranscriberAPI.get(`/api/transcriber/team`);
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};
const updateAssignedTo = async (data: any): Promise<ApiResponse<any>> => {
  try {
    const response = await TranscriberAPI.put<ApiResponse<any>>(
      "/api/transcriber/assigned/update",
      data,
    );
    return response.data;
  } catch (error) {
    throw new Error("Unable to update assigned To. Please try again later.");
  }
};

const transcriberReportStatusUpdate = async (
  data: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await TranscriberAPI.put<ApiResponse<any>>(
      "/api/transcriber/report/status/update",
      data,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      "Unable to update report-status To. Please try again later.",
    );
  }
};

const getTranscriberStatusLogsList = async (
  subjectQmeScheduleId: Number,
): Promise<ApiResponse<any[]> | undefined> => {
  try {
    const response = await TranscriberAPI.get(
      `/api/transcriber/status/updates?subjectQmescheduleId=${subjectQmeScheduleId}`,
    );

    return {
      ...response.data,
      result:
        response.data.result?.map((item: any) => ({
          ...item,
          doctorQMEStatusName: item.transcriberStatusName,
          createdDateNew: dateTimeFormatted(item.createdDate),
        })) || [],
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export {
  getTranscriberSummary,
  getTranscriberSimpleSearch,
  GetAssignedToList,
  updateAssignedTo,
  transcriberReportStatusUpdate,
  getTranscriberStatusLogsList,
};
